import { z } from 'zod';
import {
    CapacityLevels,
    Directions,
    EquipmentTypes,
    GeographyTypes,
    TimeTypes,
} from './mc-charts.enum';
import { ElementRef } from '@angular/core';

const TimeFrameSchema = z.enum([
    'THIRTY_DAYS_BACK',
    'EIGHT_DAYS_BACK',
    'PREV_BUSINESS_DAY',
    'CURRENT',
    'EIGHT_DAY_FORECAST',
    '',
]);

export type TimeFrame = z.infer<typeof TimeFrameSchema>;

export const DirectionSchema = z.enum(['IN','OUT']);

export type Direction = z.infer<typeof DirectionSchema>;

const EquipmentTypeSchema = z.enum([
    'VAN',
    'REEFER',
    'FLATBED',
]);

export type EquipmentType = z.infer<typeof EquipmentTypeSchema>;

const LoadTruckDataSchema = z.array(
    z.object({
        loads: z.number(),
        trucks: z.number(),
        ratio: z.number(),
        date: z.string(),
    }),
);

export type LoadTruckData = z.infer<typeof LoadTruckDataSchema>;

export const LtChartPayloadSchema = z.object({
    id: z.string(),
    hasError: z.boolean().default(false),
    loaded: z.boolean().default(false),
    timeframe: TimeFrameSchema,
    data: LoadTruckDataSchema,
    geoCode: z.string().or(z.null()).or(z.number()).default(''),
    direction: DirectionSchema,
});

export type LtChartPayload = z.infer<typeof LtChartPayloadSchema>;

const MciDataSchema = z.array(
    z.object({
        date: z.string(),
        mci: z.number().or(z.null()),
        predictedIntervalUpperBound: z.optional(z.number()),
        predictedIntervalLowerBound: z.optional(z.number()),
        capacity: z.string().default(''),
    })
)

export type MciData = z.infer<typeof MciDataSchema>;

export const MciChartPayloadSchema = z.object({
    id: z.string(),
    hasError: z.boolean().default(false),
    loaded: z.boolean().default(false),
    timeframe: TimeFrameSchema,
    geoCode: z.string().or(z.null()).or(z.number()).default(''),
    equipmentType: EquipmentTypeSchema,
    data: MciDataSchema
});

export type MciChartPayload = z.infer<typeof MciChartPayloadSchema>;

export interface IIqMcChartsStateService {
    getMciData(
        direction: Directions,
        equipmentType: EquipmentTypes,
        geographyType: GeographyTypes,
        geoCode: string,
        timeType: TimeTypes,
    ): Promise<IMciResponse>;
    getLtData(
        direction: Directions,
        equipmentType: EquipmentTypes,
        geographyType: GeographyTypes,
        geoCode: string,
        timeType: TimeTypes,
    ): Promise<ILoadTruckResponse>;
    renderMciPriorGraph(chart: ElementRef<any>, mciData: IMciResponse): void;
    renderLtGraph(chart: ElementRef<any>, ltData: LoadTruckData): void;
    renderMciForecastGraph(
        chart: ElementRef<any>,
        mciData: IMciResponse,
        equipmentType: EquipmentTypes,
    ): void;
}

export interface LoadTruckResponse {
    geoCode: string;
    loads: number;
    trucks: number;
    ratio: number;
    date: string;
}

export interface IMciData {
    geoCode: string;
    mci: number | null;
    date: string;
    predictedIntervalLowerBound?: number | null;
    predictedIntervalUpperBound?: number | null;
}

export interface IMciResponse {
    data: IMciData[];
    loaded: boolean;
    errors: Errors;
    hasErrors: boolean;
    hasData?: boolean;
}

export interface ILoadTruckData {
    geoCode: string;
    loads: number;
    trucks: number;
    ratio: number;
    date: string;
}

export interface ILoadTruckResponse {
    data: ILoadTruckData[];
    loaded: boolean;
    errors: Errors;
    hasErrors: boolean;
}

export interface IMciChartData {
    mci: number;
    date: number;
    upperInterval?: number;
    lowerInterval?: number;
    capcityLevel?: CapacityLevels;
}

export interface ILtChartData {
    loads: number;
    trucks: number;
    ratio: number | null;
    date: number;
    gaugeWidth: number;
}

export const emptyMciData: IMciData = {
    geoCode: '',
    mci: null,
    date: '',
};

export interface Errors {
    title?: string;
    body?: string;
}

export const MciResponseDataSchema = z.object({
    data: z.object({
        mci: z.array(
            z.object({
                mci: z.number(),
                geoCode: z.string(),
                date: z.string(),
            }),
        ),
    }),
    loading: z.boolean(),
});

export const MciForecastResponseDataSchema = z.object({
    data: z.object({
        mci: z.array(
            z.object({
                mci: z.number(),
                geoCode: z.string(),
                date: z.string(),
                predictedIntervalLowerBound: z.number(),
                predictedIntervalUpperBound: z.number(),
            }),
        ),
    }),
    loading: z.boolean(),
});

export const LtResponseDataSchema = z.object({
    data: z.object({
        loadTruck: z.array(
            z.object({
                geoCode: z.string(),
                loads: z.number(),
                trucks: z.number(),
                ratio: z.number(),
                date: z.string(),
            }),
        ),
    }),
    loading: z.boolean(),
});

export type MciResponseData = z.infer<typeof MciResponseDataSchema>;
export type MciForecastResponseData = z.infer<
    typeof MciForecastResponseDataSchema
>;
export type LtResponseData = z.infer<typeof LtResponseDataSchema>;

export const defaultMciErrorMessage: Errors = {
    title: 'MCI Graph Data Not Available',
    body: 'Please try again later.',
};

export const defaultLtErrorMessage: Errors = {
    title: 'LT Graph Data Not Available',
    body: 'Please try again later.',
};

export const emptyErrorMessage: Errors = {
    title: '',
    body: '',
};

export const emptyMciResponse: IMciResponse = {
    data: [emptyMciData],
    loaded: true,
    errors: defaultMciErrorMessage,
    hasErrors: false,
    hasData: false,
};

export const noSelectionMciResponse: Errors = {
        title: '',
        body: 'MCI Graph Data is not available for this selection. Select a region and filter to Last 8 or 30 Days to view.'
};

export const noSelectionMciForecastResponse: IMciResponse = {
    data: [emptyMciData],
    loaded: true,
    errors: defaultLtErrorMessage,
    hasErrors: true,
    hasData: false,
};

export const emptyLtData: ILoadTruckData = {
    geoCode: '',
    loads: 0,
    trucks: 0,
    ratio: 0,
    date: '',
};

export const emptyLtResponse: Errors = {
    title: 'LT Graph Data Not Available',
    body: 'Please try again later.',
};

export const noSelectionLtResponse: Errors = {
    title: '',
    body: 'L/T Ratio Data not available for this selection. Select a region and filter to Outbound Last 8 or 30 Days to view.',
};
